<template>
  <div
    v-if="!loading"
    class="flex flex-col"
  >
    <div>
      <div class="flex justify-between">
        <h2 class="mt-2">
          Fichiers hospitaliers
        </h2>
        <HospitalPatientFileDialog :sharings="sharings" :consumer-key="customer.consumer_key" @uploaded="handleFileUploaded()">
          <BaseButton
            v-if="sharings.length"
            primary
            class="ml-4"
            density="comfortable"
          >
            <Plus class="w-4 h-4 mr-2" />
            <span>Ajouter un fichier</span>
          </BaseButton>
        </HospitalPatientFileDialog>
      </div>
      <div v-if="!!hospitalFiles?.length" class="px-10">
        <Carousel>
          <CarouselContent>
            <CarouselItem v-for="file in hospitalFiles" :key="file.id" class="md:basis-1/3 lg:basis-1/4 py-1">
              <CustomerHospitalFileCard :file="file" @click="(_file) => handleFileClick(_file)" />
            </CarouselItem>
            <CarouselItem v-for="setting in fileSettings" :key="setting.id" class="md:basis-1/3 lg:basis-1/4 py-1">
              <ProgramConsumerSettingFileCard
                :setting="setting"
                @click="(_setting) => handleFileSettingsClick(_setting)"
              />
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
      <div
        v-else
        class="my-4 p-4 bg-white rounded-lg"
      >
        <span>Ce patient ne possède aucun documents hospitalier.</span>
      </div>
    </div>
  </div>
  <div
    v-else
    class="mt-4"
  >
    <base-flex-spinner />
  </div>
</template>

<script lang="ts" setup>
import useSwr from "@/core/composables/swr/useSwr";
import Customer from "@/customer/interfaces/Customer";
import {computed} from "vue";
import {hospitalFileApi, programApi} from "@/container";
import {HospitalPatientFile} from "@/core/interfaces/hospital/HospitalPatientFile";
import {SupportProgramConsumerSettings} from "@/program/interfaces/Program";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import ProgramConsumerSettingFileCard
    from "@/program/components/program-consumer-settings/ProgramConsumerSettingFileCard.vue";
import CustomerHospitalFileCard from "@/customer/components/CustomerHospitalFileCard.vue";
import {Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious} from "@/core/components/ui/carousel";
import HospitalPatientFileDialog from "@/hospital/components/hospital-patient-file/HospitalPatientFileDialog.vue";
import {Sharing} from "@/core/interfaces/Sharing";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Plus} from "@lucide/vue";

const emits = defineEmits<{
    (e: 'uploaded'): void;
}>();

const props = defineProps<{
    customer: Customer
    sharings: Sharing[]
}>();

const {data: hospitalFiles, isLoading: isLoadingFiles, mutate} = useSwr(
    computed(() => `consumers/${props.customer.consumer_key}/hospital-files`),
    () => hospitalFileApi().index(props.customer.consumer_key)
);

const {data: fileSettings, isLoading: isLoadingSetting} = useSwr(
    computed(() => `consumers/${props.customer.consumer_key}/support-program-consumer-settings`),
    () => programApi().consumerSupportProgramSettings(props.customer.consumer_key, {secure_file: true})
);

const loading = computed(() => isLoadingFiles.value || isLoadingSetting.value);

const handleFileUploaded = () => {
    emits('uploaded');
    mutate();
};

/**
 * Download the file.
 */
const handleFileClick = async (file: HospitalPatientFile) => {
    return hospitalFileApi().download(file)
        .then((response) => {
            const url = URL.createObjectURL(response);
            window.open(url);
        });
};

const handleFileSettingsClick = async (setting: SupportProgramConsumerSettings) => {
    return programApi().downloadProgramSettingFile(setting.id)
        .then((response) => {
            const url = URL.createObjectURL(response);
            window.open(url);
        });
};
</script>
